import * as Sentry from "@sentry/angular";
import { TraceService } from "@sentry/angular";
import { EnvironmentProviders, ErrorHandler, inject, provideAppInitializer, Provider } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";

/**
 * @see https://docs.sentry.io/platforms/javascript/guides/angular/
 */
export function init(): Array<Provider | EnvironmentProviders> {
  if (environment.production) {
    // Disabling console logs on prod builds
    window.console.log = () => {};
    window.console.warn = () => {};

    Sentry.init({
      dsn: "https://f1d51cca2dc0f09ecedaab5be3a24d01@o251970.ingest.us.sentry.io/4508693125070848",
      integrations: [
        // Registers and configures the Tracing integration,
        // which automatically instruments your application to monitor its
        // performance, including custom Angular routing instrumentation
        Sentry.browserTracingIntegration(),
        // Registers the Replay integration,
        // which automatically captures Session Replays
        Sentry.replayIntegration(),
      ],

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for tracing.
      // We recommend adjusting this value in production
      // Learn more at
      // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
      tracesSampleRate: 1.0,

      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/lmkwitg-bda\.srv\.mwn\.de\/api/],

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      // Learn more at
      // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });

    return [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        inject(TraceService);
      }),
    ];
  }

  return [];
}

export function createLogger(namespace: string) {
  function warn(error: Error): void;
  function warn(message: string, ...optionalParams: any[]): void;
  function warn(errorOrMessage: Error | string, ...optionalParams: any[]) {
    const parts = [`[${namespace}]`];

    if (errorOrMessage instanceof Error) {
      parts.push(errorOrMessage.message);
      // Sentry.captureException(errorOrMessage);
    } else {
      parts.push(errorOrMessage);
    }

    const message = parts.join(' ');

    if (environment.production) {
      Sentry.captureException(message, {
        extra: { optionalParams },
      });
    }

    if (optionalParams) {
      console.warn(message, optionalParams);
    } else {
      console.warn(message);
    }
  }

  return {
    log: (message: string, ...optionalParams: any[]) => console.log(`[${namespace}] ${message}`, ...optionalParams),
    warn,
  };
}
